import { useEffect, useId, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Copy } from 'lucide-react';
import { resourceClonesApi, type ClonePreflight, type CloneResult, type CloneSource } from '../../services/resourceClonesApi';
import { tenantApi, type InboundIntegration } from '../../services/tenantApi';
import { WorkflowInboundIntegrationSelect } from './WorkflowInboundIntegrationSelect';
import './ResourceCloneModal.css';

interface ResourceCloneModalProps {
  source: CloneSource;
  onClose: () => void;
  onCreated: (result: CloneResult) => void | Promise<void>;
  onOpen?: (result: CloneResult, destinationOwnerWorkflowId?: string) => void | Promise<void>;
}

export function ResourceCloneModal({ source, onClose, onCreated, onOpen }: ResourceCloneModalProps) {
  const [name, setName] = useState(`${source.name} (copy)`);
  const [names, setNames] = useState<Record<string, string>>({});
  const [ownerId, setOwnerId] = useState('');
  const [owners, setOwners] = useState<{ workflowId: string; name: string }[]>([]);
  const choosesInbound = source.rootKind === 'workflow' && !source.isSubWorkflow && !source.requiresOwner;
  const [inboundId, setInboundId] = useState(source.inboundIntegrationId ?? '');
  const [integrations, setIntegrations] = useState<InboundIntegration[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(choosesInbound);
  const [integrationError, setIntegrationError] = useState('');
  const [integrationLoad, setIntegrationLoad] = useState(0);
  const [preflight, setPreflight] = useState<ClonePreflight | null>(null);
  const [result, setResult] = useState<CloneResult | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const requestId = useRef<string | null>(null);
  const inFlight = useRef(false);
  const panel = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  const [theme] = useState(() => {
    const trigger = document.activeElement;
    if (!(trigger instanceof HTMLElement)) return {};
    const computed = getComputedStyle(trigger);
    const values: Record<string, string> = { fontFamily: computed.fontFamily };
    for (const token of ['surface', 'background', 'border', 'text-primary', 'text-secondary', 'primary', 'danger']) {
      const property = `--color-${token}`;
      const value = computed.getPropertyValue(property).trim();
      if (value) values[property] = value;
    }
    return values as CSSProperties;
  });

  useEffect(() => {
    const previous = document.activeElement;
    const overlay = panel.current?.parentElement;
    const siblings = Array.from(document.body.children).filter((element): element is HTMLElement =>
      element instanceof HTMLElement && element !== overlay);
    const previousInert = siblings.map(element => element.inert);
    const previousOverflow = document.body.style.overflow;
    siblings.forEach(element => { element.inert = true; });
    document.body.style.overflow = 'hidden';
    const focusInside = (event: FocusEvent) => {
      if (!panel.current?.contains(event.target as Node)) panel.current?.querySelector<HTMLElement>('input,button')?.focus();
    };
    document.addEventListener('focusin', focusInside);
    panel.current?.querySelector<HTMLInputElement>('input')?.focus();
    return () => {
      document.removeEventListener('focusin', focusInside);
      siblings.forEach((element, index) => { element.inert = previousInert[index]; });
      document.body.style.overflow = previousOverflow;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    if (!source.requiresOwner) return;
    let active = true;
    tenantApi.getWorkflows().then(items => {
      if (active) setOwners(items);
    }).catch((failure: unknown) => {
      if (active) setError(failure instanceof Error ? failure.message : 'Destination workflows could not be loaded.');
    });
    return () => { active = false; };
  }, [source.requiresOwner]);

  useEffect(() => {
    if (!choosesInbound) return;
    let active = true;
    setLoadingIntegrations(true);
    setIntegrationError('');
    tenantApi.getInboundIntegrations().then(items => {
      if (active) setIntegrations(items);
    }).catch((failure: unknown) => {
      if (active) setIntegrationError(failure instanceof Error ? failure.message : 'Inbound integrations could not be loaded.');
    }).finally(() => { if (active) setLoadingIntegrations(false); });
    return () => { active = false; };
  }, [choosesInbound, integrationLoad]);

  const invalidate = () => {
    setPreflight(null);
    requestId.current = null;
    setError('');
  };
  const payload = {
    rootKind: source.rootKind, rootId: source.rootId, rootName: name.trim(),
    dependencyNames: { ...names, [source.rootId]: name.trim() },
    ...(ownerId ? { destinationOwnerWorkflowId: ownerId } : {}),
    ...(choosesInbound ? { destinationInboundAssociation: { inboundIntegrationId: inboundId || null } } : {}),
  };
  const preview = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const next = await resourceClonesApi.preflight(payload);
      setNames(Object.fromEntries(next.nodes.filter(node => node.sourceId.toLowerCase() !== source.rootId.toLowerCase())
        .map(node => [node.sourceId, node.name])));
      if (next.errors.length) throw new Error(next.errors.join('\n'));
      setPreflight(next);
      requestId.current ??= crypto.randomUUID();
    } catch (failure) {
      setPreflight(null);
      setError(failure instanceof Error ? failure.message : 'Clone preflight failed. Please retry.');
    } finally { inFlight.current = false; setBusy(false); }
  };
  const confirm = async () => {
    if (!preflight || !requestId.current || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try {
      const created = await resourceClonesApi.confirm({
        ...payload, preflightToken: preflight.preflightToken, requestId: requestId.current,
      });
      setResult(created);
      try { await onCreated(created); }
      catch (failure) {
        setError(`Clone created, but the list could not be refreshed: ${failure instanceof Error ? failure.message : 'Please reload the list.'}`);
      }
    } catch (failure) {
      const message = failure instanceof Error ? failure.message : 'Clone could not be confirmed.';
      setError(`${message} No success has been confirmed here. Retry confirmation with the same request identity to recover a possibly completed request. Review again if the source changed.`);
    } finally { inFlight.current = false; setBusy(false); }
  };
  const openCreated = async () => {
    if (!result || !onOpen || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setError('');
    try { await onOpen(result, result.destinationOwnerWorkflowId ?? (ownerId || undefined)); onClose(); }
    catch (failure) { setError(failure instanceof Error ? failure.message : 'Created resource could not be opened. Reload the list to retry.'); }
    finally { inFlight.current = false; setBusy(false); }
  };

  return createPortal(
    <div className="resource-clone-overlay" style={theme} onMouseDown={event => {
      if (event.target === event.currentTarget && !busy) onClose();
    }}>
      <div className="resource-clone-modal" ref={panel} role="dialog" aria-modal="true"
        aria-labelledby={titleId} aria-describedby={descriptionId}
        onKeyDown={event => {
          if (event.key === 'Escape') { event.stopPropagation(); if (!busy) onClose(); }
          if (event.key === 'Tab') {
            const controls = Array.from(panel.current?.querySelectorAll<HTMLElement>('input:not(:disabled), select:not(:disabled), button:not(:disabled), a[href]') ?? []);
            const first = controls[0], last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }
        }}>
        <h2 id={titleId}>Clone {source.name}</h2>
        <p id={descriptionId}>{source.rootKind === 'workflow'
          ? 'Copy the workflow contents and called sub-workflows independently. Choose an existing inbound integration or Global / legacy (no association) for a top-level workflow. All other external integration/action/schema bindings are retained; no integrations are created. Sub-workflows inherit their caller context. Integration credentials stay with the existing integrations. All workflow copies start disabled; execution history is not copied.'
          : 'Copy the full integration and workflow configuration graph independently. All copies start disabled. Credentials must be entered again; inbound keys and execution history are not copied.'}
          {' '}Users, groups, schedules, notification channels and provider accounts remain external references.</p>
        {error && <p role="alert">{error}</p>}
        {result ? <section role="status" aria-label="Clone created">
          <h3>{result.rootName} created</h3>
          <p>{source.rootKind === 'workflow'
            ? 'Created disabled with the selected inbound association and all other external references retained. Review the copied workflow before explicitly enabling it.'
            : 'Created disabled. Review configuration and supply required credentials before explicitly enabling any copy.'}</p>
          <ul>{result.nodes.map(node => <li key={`${node.kind}:${node.sourceId}`}>{node.kind}: {node.name}</li>)}</ul>
          {onOpen && <button type="button" disabled={busy} onClick={() => void openCreated()}>Open cloned resource</button>}
        </section> : <>
          <label>New name<input aria-label="Clone name" value={name} disabled={busy}
            onChange={event => { setName(event.target.value); invalidate(); }} /></label>
          {choosesInbound && <>
            <WorkflowInboundIntegrationSelect value={inboundId} integrations={integrations}
              disabled={busy || loadingIntegrations || !!integrationError}
              onChange={value => { setInboundId(value); invalidate(); }} />
            {loadingIntegrations && <p role="status">Loading inbound integrations...</p>}
            {integrationError && <p role="alert">{integrationError}{' '}
              <button type="button" disabled={busy} onClick={() => setIntegrationLoad(value => value + 1)}>Retry loading integrations</button>
            </p>}
            <p>The source association is selected initially. Changing it does not rewrite step mappings or other external references.
              Trigger and called sub-workflow compatibility are checked during review.</p>
          </>}
          {source.requiresOwner && <label>Destination workflow<select aria-label="Clone destination workflow"
            value={ownerId} disabled={busy} onChange={event => { setOwnerId(event.target.value); invalidate(); }}>
            <option value="">Select a destination (compatibility checked in preflight)</option>
            {owners.map(owner => <option key={owner.workflowId} value={owner.workflowId}>{owner.name}</option>)}
          </select></label>}
          {Object.keys(names).length > 0 && <fieldset disabled={busy}><legend>Dependency names</legend>
            {Object.entries(names).map(([id, value]) => <label key={id}>Dependency: {value || id}
              <input aria-label={`Clone dependency ${id}`} value={value}
                onChange={event => { setNames(current => ({ ...current, [id]: event.target.value })); invalidate(); }} />
            </label>)}
          </fieldset>}
          {preflight && <section aria-label="Clone preflight summary">
            <h3>Configuration to copy</h3>
            <ul>{preflight.nodes.map(node => <li key={`${node.kind}:${node.sourceId}`}>{node.kind}: {node.name}</li>)}</ul>
            <h3>Retained external references</h3>
            {preflight.externalReferences.length ? <ul>{preflight.externalReferences.map((reference, index) => <li key={index}>{reference}</li>)}</ul> : <p>None.</p>}
            <h3>Requirements</h3>
            <ul>{preflight.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul>
          </section>}
        </>}
        <div className="resource-clone-actions">
          <button type="button" disabled={busy} onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result && preflight && <button type="button" disabled={busy} onClick={() => void preview()}>Review again</button>}
          {!result && <button type="button" disabled={busy || loadingIntegrations || !!integrationError || !name.trim() || source.requiresOwner && !ownerId ||
            Object.values(names).some(value => !value.trim())}
            onClick={() => void (preflight ? confirm() : preview())}>
            {busy ? 'Working...' : preflight ? 'Confirm clone' : 'Review clone'}
          </button>}
        </div>
      </div>
    </div>, document.body);
}

export function ResourceCloneButton({ source, onCreated, onOpen }: Omit<ResourceCloneModalProps, 'onClose'>) {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className="btn-icon" aria-label={`Clone ${source.name}`} title="Clone"
      onClick={() => setOpen(true)}><Copy size={16} /></button>
    {open && <ResourceCloneModal source={source} onClose={() => setOpen(false)} onCreated={onCreated} onOpen={onOpen} />}
  </>;
}
